import { Link } from 'react-router-dom';
import { Badge, Chip } from '@zarya/ui';
import type { components } from '@app/api/types';
import { t } from '@app/i18n';
import { identityDrawerHref } from '@app/navigation';
import s from './Runtime.module.css';

type DeclarationDifference = components['schemas']['DeclarationDifference'];
type DeclaredCapability = components['schemas']['DeclaredCapability'];

/**
 * A linha «Contrato diferente da declaração da imagem» (aim-ypw, decisão 3 do mrolim, 11/10/2026).
 *
 * Aparece no card Security Ready de Status do runtime (T15) e no card Estado do Security Contract da identidade
 * (T13), com o MESMO dado do servidor (`declaration_difference`, do Postgres). Ela diz o que a imagem pede e o
 * contrato não concede, e o que o contrato concede sem a imagem pedir, e leva à aba Capabilities, onde a tabela mostra
 * as três colunas lado a lado.
 *
 * **Não conta para «pronto»**, como a rede: o contrato é a autoridade, e quem aprovou menos (ou mais) do que a imagem
 * pede tomou uma decisão legítima. Por isso o tom é de aviso, e a linha diz em voz alta que não pesa no badge.
 *
 * Sem diferença, ou sem o que comparar (`null`), a linha não aparece: nada aqui afirma «igual» sobre o que ninguém
 * comparou.
 */
export function DeclarationNotice({
  difference,
  identityId,
}: {
  difference?: DeclarationDifference | null;
  identityId: string;
}) {
  const notGranted = difference?.not_granted ?? [];
  const notDeclared = difference?.not_declared ?? [];
  if (notGranted.length === 0 && notDeclared.length === 0) return null;

  return (
    <div className={s.declaracao} data-testid="declaration-notice">
      <Badge tone="warning">{t('runtimeStatus.declaration.title')}</Badge>
      {notGranted.length > 0 ? (
        <p className={s.declaracaoLinha} data-testid="declaration-not-granted">
          {t('runtimeStatus.declaration.notGranted')} {notGranted.map(chip)}
        </p>
      ) : null}
      {notDeclared.length > 0 ? (
        <p className={s.declaracaoLinha} data-testid="declaration-not-declared">
          {t('runtimeStatus.declaration.notDeclared')} {notDeclared.map(chip)}
        </p>
      ) : null}
      <p className={s.declaracaoLinha}>
        <Link to={identityDrawerHref(identityId, 'capabilities')}>{t('runtimeStatus.declaration.link')}</Link>
        <span>{` · ${t('runtimeStatus.check.notCounted')}`}</span>
      </p>
    </div>
  );
}

/**
 * Uma capability da linha, como as abas a escrevem: a tool pelo par servidor/nome (decisão 13), e a delegação pelo
 * caminho do agente chamado. O par é dado, e não texto: não vai ao catálogo.
 */
function chip(c: DeclaredCapability) {
  const key = `${c.kind}:${c.server ?? ''}/${c.name}`;
  return c.kind === 'agent' ? (
    <Chip key={key} icon="exchange">
      invoke_agent → {c.name}
    </Chip>
  ) : (
    <Chip key={key}>{c.server ? `${c.server}/${c.name}` : c.name}</Chip>
  );
}
